'use client';
import { useEffect, useRef, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';

/**
 * Registers the push notification service worker and subscribes to push.
 * Stores the subscription on the server for server-to-push delivery.
 * Also listens for service worker messages (e.g., call accept/reject from notification actions).
 */
export function usePushNotifications(onCallAccept?: () => void, onCallReject?: () => void) {
  const { user } = useAuth();
  const registeredRef = useRef(false);

  const registerServiceWorker = useCallback(async () => {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;
    if (registeredRef.current) return;
    registeredRef.current = true;

    try {
      const registration = await navigator.serviceWorker.register('/sw-push.js', { scope: '/' });
      await navigator.serviceWorker.ready;

      // Request notification permission
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') return;

      // Check if already subscribed
      let subscription = await registration.pushManager.getSubscription();

      if (!subscription) {
        // Generate VAPID public key (we'll use a simple approach)
        // For production, you'd use proper VAPID keys from env
        const vapidPublicKey = process.env.NEXT_PUBLIC_VAPID_PUBLIC_KEY;
        
        if (vapidPublicKey) {
          const convertedKey = urlBase64ToUint8Array(vapidPublicKey);
          subscription = await registration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: convertedKey,
          });
        } else {
          // Fallback: subscribe without VAPID (will still work for same-origin notifications)
          subscription = await registration.pushManager.subscribe({
            userVisibleOnly: true,
          });
        }
      }

      // Send subscription to server
      if (subscription) {
        await fetch('/api/push-subscription', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ subscription: subscription.toJSON() }),
        });
      }
    } catch (err) {
      console.warn('Push notification registration failed:', err);
    }
  }, []);

  // Listen for messages from the service worker
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;

    const handleMessage = (event: MessageEvent) => {
      const { type } = event.data || {};
      if (type === 'call_accept' && onCallAccept) onCallAccept();
      if (type === 'call_reject' && onCallReject) onCallReject();
      if (type === 'call_dismissed' && onCallReject) onCallReject();
    };

    navigator.serviceWorker.addEventListener('message', handleMessage);
    return () => navigator.serviceWorker.removeEventListener('message', handleMessage);
  }, [onCallAccept, onCallReject]);

  // Register when user logs in
  useEffect(() => {
    if (user) {
      registerServiceWorker();
    }
  }, [user, registerServiceWorker]);
}

// Utility: Convert VAPID key from base64 URL to Uint8Array
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i++) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}
